import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RewardSettings, UpdateRewardSettingsRequest } from '../../employee/models/employee.models';

/**
 * Modal component for configuring reward settings (membership reward, wash threshold and wash reward) with built-in validation, clamping, and form controls.
 * Provides a user interface to view and edit reward-related values and emits structured update requests when saved.
 * Key behaviors include form initialization/population from initial data, validation against centralized thresholds, increment/decrement helpers, paste/keyboard sanitation for numeric inputs, and controlled closing with animation delay.
 * Lifecycle hooks:
 *   - ngOnInit: initializes the reactive form.
 *   - ngOnChanges: populates the form when initialData changes and resets modal state when opened.
 * Important methods:
 *   - initializeForm(): builds the reactive FormGroup with validators and optionally populates it from initialData.
 *   - populateForm(): patches the form with values from initialData.
 *   - onSave(): validates the form, transforms form values to API shape (UpdateRewardSettingsRequest) and emits saveSettings; marks fields as touched when invalid.
 *   - onClose(): triggers a close with a short delay to allow animations, then emits closeModal.
 *   - onCancel(): alias for onClose to dismiss the modal without saving.
 *   - incrementValue()/decrementValue(): increase or decrease numeric fields with clamping to configured min/max.
 *   - getFieldError(): returns user-facing validation messages for a field (required and range checks).
 *   - onNumberKeyDown(), onNumberInput(), onNumberPaste(): prevent/clean invalid numeric input and handle pasted content appropriately.
 *   - onFieldBlur(): clamps values on blur to the configured min/max and marks the field as touched.
 *   - resetSubmittingState(): called by parent to clear submitting flag after async save completes.
 *   - resetModalState(): internal reset of submitting/closing flags and form pristine/untouched state when opening.
 * @component
 * @selector app-reward-settings-modal
 * @example
 *   <app-reward-settings-modal
 *     [isOpen]="true"
 *     [initialData]="rewardSettings"
 *     (closeModal)="handleClose()"
 *     (saveSettings)="handleSave($event)">
 *   </app-reward-settings-modal>
 * @input {boolean} isOpen - Controls whether the modal is visible/open.
 * @input {RewardSettings | null} initialData - Optional initial reward settings used to populate the form (expects membership_reward, wash_count, threshold_reward).
 * @output {EventEmitter<void>} closeModal - Emitted when the modal is closed (fired after a short animation delay).
 * @output {EventEmitter<UpdateRewardSettingsRequest>} saveSettings - Emitted when a valid save occurs; payload is transformed into API shape: { membership_reward: number, wash_count: number, threshold_reward: number }.
 */
@Component({
  selector: 'app-reward-settings-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './reward-settings-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./reward-settings-modal.component.scss'],
})
export class RewardSettingsModalComponent implements OnInit, OnChanges {
  @Input() isOpen = false;
  @Input() initialData: RewardSettings | null = null;
  @Output() closeModal = new EventEmitter<void>();
  @Output() saveSettings = new EventEmitter<UpdateRewardSettingsRequest>();

  rewardForm!: FormGroup;
  isSubmitting = false;
  isClosing = false;

  // Validation thresholds (single source of truth)
  readonly membershipRewardMin = 0.01;
  readonly membershipRewardMax = 200;
  readonly numberOfWashesMin = 1;
  readonly numberOfWashesMax = 500;
  readonly washRewardMin = 0.01;
  readonly washRewardMax = 200;

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialData'] && this.rewardForm && this.initialData) {
      this.populateForm();
    }

    // Reset state when modal opens
    if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
      this.resetModalState();
    }
  }

  /**
   * Initialize and configure the reactive rewardForm FormGroup with validation rules and populate it when initial data is present.
   * @example
   * initializeForm()
   * undefined
   * @param {{void}} {{none}} - No arguments are required.
   * @returns {{void}} Return description in one line.
   **/
  initializeForm() {
    this.rewardForm = this.fb.group({
      membershipRewardAmount: [
        '',
        [
          Validators.required,
          Validators.min(this.membershipRewardMin),
          Validators.max(this.membershipRewardMax),
        ],
      ],
      numberOfWashesThreshold: [
        '',
        [
          Validators.required,
          Validators.min(this.numberOfWashesMin),
          Validators.max(this.numberOfWashesMax),
        ],
      ],
      washRewardAmount: [
        '',
        [
          Validators.required,
          Validators.min(this.washRewardMin),
          Validators.max(this.washRewardMax),
        ],
      ],
    });

    // Populate form if initial data is available
    if (this.initialData) {
      this.populateForm();
    }
  }

  populateForm() {
    if (this.initialData && this.rewardForm) {
      this.rewardForm.patchValue({
        membershipRewardAmount: this.initialData.membership_reward.toString(),
        numberOfWashesThreshold: this.initialData.wash_count.toString(),
        washRewardAmount: this.initialData.threshold_reward.toString(),
      });
    }
  }

  onClose() {
    if (!this.isSubmitting && !this.isClosing) {
      this.isClosing = true;
      // Wait for animation to complete before emitting close
      setTimeout(() => {
        this.closeModal.emit();
        this.isClosing = false;
      }, 300);
    }
  }

  onCancel() {
    this.onClose();
  }

  /**
   * Validate the reward settings form, transform its values to the API payload shape, emit the saveSettings event when valid, or mark controls as touched to show validation errors.
   * @example
   * nSave()
   * undefined
   * @returns {{void}} No direct return; emits saveSettings with transformed payload or marks form controls as touched.
   **/
  onSave() {
    if (this.rewardForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;

      // Transform form data to match API format
      const formData = this.rewardForm.value;
      const apiData: UpdateRewardSettingsRequest = {
        membership_reward: Number(formData.membershipRewardAmount),
        wash_count: parseInt(formData.numberOfWashesThreshold, 10),
        threshold_reward: Number(formData.washRewardAmount),
      };

      this.saveSettings.emit(apiData);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.rewardForm.controls).forEach(key => {
        this.rewardForm.get(key)?.markAsTouched();
      });
    }
  }

  // Called from parent component to reset form state
  resetSubmittingState() {
    this.isSubmitting = false;
  }

  // Reset modal state when opening
  private resetModalState() {
    this.isSubmitting = false;
    this.isClosing = false;

    // Reset form validation state
    if (this.rewardForm) {
      this.rewardForm.markAsUntouched();
      this.rewardForm.markAsPristine();
    }
  }

  /**
   * Return a human-readable validation error message for the specified form field.
   * @example
   * etFieldError('membershipRewardAmount')
   * 'Value must be between $5 and $25'
   * @param {{string}} {{fieldName}} - The name of the form control to check for validation errors.
   * @returns {{string}} The error message for the field or an empty string if there is no visible error.
   **/
  getFieldError(fieldName: string): string {
    const field = this.rewardForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldDisplayName(fieldName)} is required`;

      // Range validation messages based on current thresholds
      const { min, max } = this.getFieldRange(fieldName);
      if ((field.errors['min'] || field.errors['max']) && min !== undefined && max !== undefined) {
        if (fieldName === 'membershipRewardAmount' || fieldName === 'washRewardAmount') {
          return `Value must be between $${min} and $${max}`;
        }
        if (fieldName === 'numberOfWashesThreshold') {
          return `Value must be between ${min} and ${max}`;
        }
      }
    }
    return '';
  }

  /**
   * Increment the numeric value of a reward form field by a fixed step, up to its configured maximum.
   * @example
   * ncrementValue('membershipRewardAmount')
   * undefined
   * @param {string} fieldName - The name of the form control to increment (e.g., 'membershipRewardAmount' or 'washRewardAmount').
   * @returns {void} Updates the form control value in-place (increments by step, clamps to max) and marks the control as touched.
   */
  incrementValue(fieldName: string) {
    const field = this.rewardForm.get(fieldName);
    if (field) {
      const isDecimal = fieldName === 'membershipRewardAmount' || fieldName === 'washRewardAmount';
      const currentValue = isDecimal
        ? parseFloat(field.value) || 0
        : parseInt(field.value, 10) || 0;
      const { max } = this.getFieldRange(fieldName);
      const maxValue = max ?? 1000;
      const step = isDecimal ? 1 : 1;

      if (currentValue < maxValue) {
        const newValue = Math.min(Number((currentValue + step).toFixed(2)), maxValue);
        field.setValue(newValue);
        field.markAsTouched();
      }
    }
  }

  /**
   * Decrement the numeric value of a form control by one, respecting decimal fields and its minimum.
   * @example
   * ecrementValue('membershipRewardAmount')
   * undefined
   * @param {{string}} {{fieldName}} - Name of the form control to decrement.
   * @returns {{void}} No return value; updates the form control value if present and above its minimum.
   **/
  decrementValue(fieldName: string) {
    const field = this.rewardForm.get(fieldName);
    if (field) {
      const isDecimal = fieldName === 'membershipRewardAmount' || fieldName === 'washRewardAmount';
      const currentValue = isDecimal
        ? parseFloat(field.value) || 0
        : parseInt(field.value, 10) || 0;
      const { min } = this.getFieldRange(fieldName);
      const minValue = min ?? 1;
      const step = isDecimal ? 1 : 1;

      if (currentValue > minValue) {
        const newValue = Math.max(Number((currentValue - step).toFixed(2)), minValue);
        field.setValue(newValue);
        field.markAsTouched();
      }
    }
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: Record<string, string> = {
      membershipRewardAmount: 'Membership Reward Amount',
      numberOfWashesThreshold: 'Number of Washes Threshold',
      washRewardAmount: 'Wash Reward Amount',
    };
    return displayNames[fieldName] || fieldName;
  }

  /**
   * Get the min and max numeric range for a specified reward settings field.
   * @example
   * getFieldRange('membershipRewardAmount')
   * { min: 10, max: 100 }
   * @param {{string}} fieldName - The field name to query ('membershipRewardAmount', 'numberOfWashesThreshold', or 'washRewardAmount').
   * @returns {{ min?: number; max?: number }} Returns an object with optional min and max numbers for the field, or an empty object if the field is not recognized.
   **/
  private getFieldRange(fieldName: string): { min?: number; max?: number } {
    switch (fieldName) {
      case 'membershipRewardAmount':
        return { min: this.membershipRewardMin, max: this.membershipRewardMax };
      case 'numberOfWashesThreshold':
        return { min: this.numberOfWashesMin, max: this.numberOfWashesMax };
      case 'washRewardAmount':
        return { min: this.washRewardMin, max: this.washRewardMax };
      default:
        return {};
    }
  }

  // Prevent invalid characters in number inputs
  /**
   * Prevent non-numeric keys (and optionally decimal separator) during keydown for numeric inputs.
   * @example
   * nNumberKeyDown(new KeyboardEvent('keydown', { key: '1', keyCode: 49 }), true)
   * undefined
   * @param {{KeyboardEvent}} {{event}} - The keydown event to evaluate and potentially prevent.
   * @param {{boolean}} {{allowDecimal}} - Whether to allow decimal separator keys (defaults to true).
   * @returns {{void}} Returns nothing; may call event.preventDefault() for disallowed keys.
   **/
  onNumberKeyDown(event: KeyboardEvent, allowDecimal = true): void {
    // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
    if (
      [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(event.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true)
    ) {
      return;
    }

    const isNumberKey =
      (!event.shiftKey && event.keyCode >= 48 && event.keyCode <= 57) ||
      (event.keyCode >= 96 && event.keyCode <= 105);

    const isDecimalKey =
      allowDecimal && (event.key === '.' || event.keyCode === 190 || event.keyCode === 110);

    if (!isNumberKey && !isDecimalKey) {
      event.preventDefault();
    }
  }

  // Clean up any invalid characters that might have been pasted
  /**
   * Handle numeric input events for reward form controls: marks the related FormControl as touched and updates its validation without modifying the typed value.
   * @example
   * nNumberInput({ target: { value: '123', getAttribute: (name) => 'points' } })
   * undefined
   * @param {any} event - DOM input event whose target contains value and formControlName attributes.
   * @returns {void} No return value; updates form control state (markAsTouched and updateValueAndValidity).
   */
  onNumberInput(event: any): void {
    const input = event.target;
    const formControlName = input.getAttribute('formControlName');

    if (!formControlName) return;

    // Remove any non-numeric characters
    // const cleanValue = value.replace(/[^0-9]/g, '');

    // // Update the input value if it was changed
    // if (value !== cleanValue) {
    //   input.value = cleanValue;
    //   this.rewardForm.get(formControlName)?.setValue(cleanValue);
    // }

    // Mark field as touched to show validation errors (but don't clamp during typing)
    this.rewardForm.get(formControlName)?.markAsTouched();
    this.rewardForm.get(formControlName)?.updateValueAndValidity();
  }

  // Handle field blur to validate and clamp values
  /**
   * Validate and clamp a numeric form field value on blur, then mark the field as touched.
   * @example
   * nFieldBlur('rewardAmount', 1, 100)
   * undefined
   * @param {string} fieldName - The name of the form control in this.rewardForm to validate.
   * @param {number} minValue - Minimum allowed numeric value (inclusive).
   * @param {number} maxValue - Maximum allowed numeric value (inclusive).
   * @returns {void} No return value.
   */
  onFieldBlur(fieldName: string, minValue: number, maxValue: number): void {
    const field = this.rewardForm.get(fieldName);
    if (field) {
      const value = field.value;
      const numValue = parseInt(value, 10);

      if (value && !isNaN(numValue)) {
        if (numValue < minValue) {
          field.setValue(minValue.toString());
        } else if (numValue > maxValue) {
          field.setValue(maxValue.toString());
        }
      }
      field.markAsTouched();
    }
  }

  // Handle paste events to clean up pasted content
  /**
   * Handle paste into a numeric input: prevent default paste, sanitize to digits (optionally decimals), clamp to the field's min/max, and update the input and form control.
   * @example
   * nNumberPaste(event, true)
   * void
   * @param {{ClipboardEvent}} {{event}} - Paste clipboard event from the input element.
   * @param {{boolean}} {{allowDecimal}} - Whether to allow a decimal point in the pasted value (default: false).
   * @returns {{void}} No return value; updates the input and form control directly.
   **/
  onNumberPaste(event: ClipboardEvent, allowDecimal = false): void {
    event.preventDefault();

    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text');
      const cleanText = pastedText.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '');

      if (cleanText) {
        const input = event.target as HTMLInputElement;
        const formControlName = input.getAttribute('formControlName');

        if (formControlName) {
          // Get min and max values based on field
          const { min, max } = this.getFieldRange(formControlName);
          const minValue = min ?? 1;
          const maxValue = max ?? 1000;

          // Clamp the value to valid range
          const numValue = allowDecimal ? parseFloat(cleanText) : parseInt(cleanText, 10);
          let finalValue = cleanText;

          if (!isNaN(numValue as number)) {
            if (numValue < minValue) {
              finalValue = minValue.toString();
            } else if (numValue > maxValue) {
              finalValue = maxValue.toString();
            }
          }

          input.value = finalValue;
          this.rewardForm.get(formControlName)?.setValue(finalValue);
          this.rewardForm.get(formControlName)?.markAsTouched();
        }
      }
    }
  }
}
